import React, { useState, useEffect } from 'react';
import { startOfISOWeek, endOfISOWeek } from 'date-fns';
import styled, { css } from 'styled-components';
import { CustomSelect } from './CustomSelect';
import { PeriodFilter } from './PeriodFilter';
import WeekDatePicker from './WeekDateFilter';
import DateModal from './DateModal';
import RegionModal from './RegionModal';
import UsageModal from './UsageModal';

type Region = { current_ctp_cd: string; ctp_kor_nm_abbr: string };

const REGION_LIST: Region[] = [
  { ctp_kor_nm_abbr: '서울', current_ctp_cd: '11' },
  { ctp_kor_nm_abbr: '부산', current_ctp_cd: '26' },
  { ctp_kor_nm_abbr: '대구', current_ctp_cd: '27' },
  { ctp_kor_nm_abbr: '인천', current_ctp_cd: '28' },
  { ctp_kor_nm_abbr: '광주', current_ctp_cd: '29' },
  { ctp_kor_nm_abbr: '대전', current_ctp_cd: '30' },
  { ctp_kor_nm_abbr: '울산', current_ctp_cd: '31' },
  { ctp_kor_nm_abbr: '세종', current_ctp_cd: '36' },
  { ctp_kor_nm_abbr: '경기', current_ctp_cd: '41' },
  { ctp_kor_nm_abbr: '강원', current_ctp_cd: '51' },
  { ctp_kor_nm_abbr: '충북', current_ctp_cd: '43' },
  { ctp_kor_nm_abbr: '충남', current_ctp_cd: '44' },
  { ctp_kor_nm_abbr: '전북', current_ctp_cd: '52' },
  { ctp_kor_nm_abbr: '전남', current_ctp_cd: '46' },
  { ctp_kor_nm_abbr: '경북', current_ctp_cd: '47' },
  { ctp_kor_nm_abbr: '경남', current_ctp_cd: '48' },
  { ctp_kor_nm_abbr: '제주', current_ctp_cd: '50' },
];

type CommonFilterProps = {
  date: { from: Date; to: Date };
  setDate: React.Dispatch<React.SetStateAction<{ from: Date; to: Date }>>;
  filterType: string;
  setFilterType: (type: string) => void;
  court: string;
  setCourt: (court: string) => void;
  regionCode: string;
  setRegionCode: (code: string) => void;
  regionName: string;
  setRegionName: (region: string) => void;
  usage: string;
  setUsage: (usage: string) => void;
  usageDetail: string;
  setUsageDetail: (usageDetail: string) => void;
  analysisPeriod: 'month' | 'week' | 'year';
  setAnalysisPeriod: (unit: 'month' | 'week' | 'year') => void;
  disableAll?: boolean;
};

const CommonFilter: React.FC<CommonFilterProps> = ({
  analysisPeriod,
  setAnalysisPeriod,
  date,
  setDate,
  court,
  setCourt,
  regionCode,
  setRegionCode,
  regionName,
  setRegionName,
  usage,
  setUsage,
  usageDetail,
  setUsageDetail,
  disableAll,
}) => {
  const regionList = REGION_LIST;
  const [activeTab, setActiveTab] = useState('지역');
  const [localCourt, setLocalCourt] = useState(court ?? '전체');
  const [localRegionCode, setLocalRegionCode] = useState(regionCode ?? 'all');
  const getRegionNameFromCode = (code: string) => {
    if (code === '' || code === 'all') return '전체';
    const r = regionList.find((x) => x.current_ctp_cd === code);
    return r ? r.ctp_kor_nm_abbr : code;
  };
  const [localUsage, setLocalUsage] = useState(usage ?? '전체');
  const [localUsageDetail, setLocalUsageDetail] = useState(
    usageDetail ?? '전체',
  );

  const [pickedDate, setPickedDate] = useState<Date>(date.from);
  const [localDate, setLocalDate] = useState<{ from: Date; to: Date }>(date);

  const [activeButton, setActiveButton] = useState<string | null>(null);
  const handleButtonClick = (type: string) =>
    setActiveButton((prev) => (prev === type ? null : type));

  const handleResetFilters = () => {
    const now = new Date();

    if (analysisPeriod === 'week') {
      // 주간 모드: 이번 주의 바로 전주로 초기화
      const prevRef = new Date(now);
      prevRef.setDate(prevRef.getDate() - 7); // addWeeks(now, -1)와 동일 효과
      const weekStart = startOfISOWeek(prevRef);
      const weekEnd = endOfISOWeek(prevRef);

      // 로컬 상태 초기화
      setLocalCourt('전체');
      setLocalRegionCode('all');
      setLocalUsage('전체');
      setLocalUsageDetail('전체');
      setPickedDate(weekStart);
      setLocalDate({ from: weekStart, to: weekEnd });

      // 상위 상태 반영
      setCourt('전체');
      setRegionCode('all');
      setRegionName('전체');
      setUsage('전체');
      setUsageDetail('전체');
      setDate({ from: weekStart, to: weekEnd });

      setActiveButton(null);
      return;
    }

    // 월간/기타 모드: 이전달 전체로 초기화(기존 로직 유지)
    const prevFirst = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    setLocalCourt('전체');
    setLocalRegionCode('all');
    setLocalUsage('전체');
    setLocalUsageDetail('전체');
    setPickedDate(prevFirst);
    setLocalDate({ from: prevFirst, to: prevEnd });

    setCourt('');
    setRegionCode('');
    setRegionName('전체');
    setUsage('');
    setUsageDetail('');
    setDate({ from: prevFirst, to: prevEnd });
    setActiveButton(null);
  };

  useEffect(() => {
    setLocalDate(date);
    setPickedDate(date.from); // 주간 피커가 선택한 기준일도 동기화
  }, [date]);

  useEffect(() => {
    setRegionName(getRegionNameFromCode(regionCode));
  }, [regionCode]);

  function formatMonthLabel(from: Date, to: Date) {
    const sameYM =
      from.getFullYear() === to.getFullYear() &&
      from.getMonth() === to.getMonth();

    const fY = from.getFullYear();
    const fM = from.getMonth() + 1;
    const tY = to.getFullYear();
    const tM = to.getMonth() + 1;

    return sameYM ? `${fY}년 ${fM}월` : `${fY}년 ${fM}월 ~ ${tY}년 ${tM}월`;
  }

  // RegionModal에서 regionCode만 상위로 전달
  const handleRegionSelect = (code: string) => {
    setRegionCode(code); // 상위에 regionCode만 전달
    // regionName 동기화는 useEffect에서 처리
  };

  return (
    <Wrap>
      <ResetButton onClick={handleResetFilters} disabled={disableAll}>
        <img src="/assets/images/icon/reload-gray.svg" alt="" />
      </ResetButton>
      <Row>
        <FilterOptionButton
          type="button"
          onClick={() => handleButtonClick('period')}
          disabled={disableAll}
        >
          <span>기간</span>
          <span>
            {analysisPeriod === 'month'
              ? formatMonthLabel(date.from, date.to)
              : `${date.from.getFullYear()}년 ${date.from.getMonth() + 1}월 ${getMonthWeekNumber(date.from)}주차`}
          </span>
        </FilterOptionButton>

        <FilterOptionButton
          type="button"
          onClick={() => handleButtonClick('region')}
          disabled={disableAll}
        >
          <span>{activeTab}</span>
          <span>
            {activeTab === '지역'
              ? regionCode === '' || regionCode === 'all'
                ? '전체'
                : getRegionNameFromCode(regionCode)
              : court === ''
                ? '전체'
                : court}
          </span>
        </FilterOptionButton>

        <FilterOptionButton
          type="button"
          onClick={() => handleButtonClick('usage')}
          disabled={disableAll}
        >
          <span>용도</span>
          <span>
            {usage === ''
              ? '전체'
              : `${usage} - ${usageDetail ? usageDetail : '전체'}`}
          </span>
        </FilterOptionButton>
      </Row>

      {activeButton === 'period' && (
        <DateModal
          isMonth={analysisPeriod === 'month'}
          date={date}
          setDate={setDate}
          onClose={() => setActiveButton(null)}
        />
      )}

      {activeButton === 'region' && (
        <RegionModal
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          court={court}
          setCourt={setCourt}
          regionCode={regionCode}
          setRegionCode={handleRegionSelect}
          regionList={regionList}
          onClose={() => setActiveButton(null)}
        />
      )}

      {activeButton === 'usage' && (
        <UsageModal
          usage={usage}
          setUsage={setUsage}
          usageDetail={usageDetail}
          setUsageDetail={setUsageDetail}
          onClose={() => setActiveButton(null)}
        />
      )}
    </Wrap>
  );
};

export default CommonFilter;

function getMonthWeekNumber(date: Date): number {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const dayOfWeek = firstDayOfMonth.getDay(); // 0: 일요일 ~ 6: 토요일
  const offsetDate = date.getDate() + dayOfWeek - 1;
  return Math.ceil(offsetDate / 7);
}

const Wrap = styled.div`
  padding-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  width: none;
`;

const ResetButton = styled.button<{ disabled?: boolean }>`
  outline: none;
  display: flex;
  width: 36px;
  height: 36px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #e8e9ed;
  background: #fff;

  & > img {
    width: 18px;
    height: 18px;
  }
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  width: none;
  -ms-overflow-style: none; /* IE, Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const FilterOptionButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 36px;
  padding: 0 18px;
  min-width: 90px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 999px;
  border: 1px solid #e8e9ed;
  background-color: #fff;
  color: #0c0e12;

  cursor: pointer;
  transition: all 0.2s ease;

  span:first-child {
    color: #787d87;
  }

  span:last-child {
    color: #0c0e12;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const RowRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  right: 0;
`;

const OptionTitle = styled.span`
  color: #787d87;
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 40px;
  letter-spacing: -0.02em;
  text-align: center;
  display: inline-block;
  margin-right: 10px;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  height: 36px;
  padding-right: 14px;
  padding-left: 12px;
  gap: 4px;
  opacity: 1;
  border-radius: 8px;
  border-width: 1px;
  background: #fff;
  cursor: pointer;
  margin-right: 10px;
`;

const ApplyButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #111827;
  background: #111827;
  color: #fff;
  cursor: pointer;
`;
